import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { usePasswordResetRequestMutation } from 'services/kewloxApi';
import { PasswordResetRequestFormData } from './types';
import { Button } from 'components/ui/button';
import { Mail, CheckCircle } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import logo from 'assets/images/logo.png';

export function PasswordResetRequestPage() {
  const [passwordResetRequest, { isLoading }] =
    usePasswordResetRequestMutation();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const schema = Yup.object({
    identifier: Yup.string().required('Champ requis'),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordResetRequestFormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (values: PasswordResetRequestFormData) => {
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await passwordResetRequest(values).unwrap();
      setSuccessMessage(
        "Un email avec les instructions de réinitialisation a été envoyé si l'utilisateur existe.",
      );
    } catch (err) {
      setErrorMessage(
        "Une erreur s'est produite lors de l'envoi de la demande.",
      );
    }
  };

  return (
    <>
      <Helmet>
        <title>Réinitialiser le mot de passe</title>
        <meta name="description" content="Réinitialiser le mot de passe" />
      </Helmet>

      <div className="flex min-h-screen w-full items-center justify-center bg-gray-100 p-4 dark:bg-gray-950">
        <Card className="w-full max-w-md p-8 shadow-xl transition-shadow duration-300 hover:shadow-2xl">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-6">
              <img src={logo} alt="logo" className="mx-auto h-12 w-auto" />
            </div>
            <CardTitle className="mt-6 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-gray-50">
              Mot de passe oublié?
            </CardTitle>
            {!successMessage && (
              <CardDescription className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Entrez votre email pour recevoir un lien de réinitialisation.
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {successMessage ? (
              <div className="rounded-md bg-green-50 p-4 text-center">
                <div className="flex flex-col items-center justify-center space-y-4">
                  <CheckCircle className="h-12 w-12 text-green-500" />
                  <p className="text-sm font-medium text-green-800">
                    {successMessage}
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="identifier">Email ou nom d'utilisateur</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400" />
                    <Input
                      id="identifier"
                      placeholder="votre@email.com"
                      {...register('identifier')}
                      className="pl-10"
                    />
                  </div>
                  {errors.identifier && (
                    <p className="text-sm text-red-600">
                      {errors.identifier.message}
                    </p>
                  )}
                </div>
                {errorMessage && (
                  <p className="text-sm text-red-600">{errorMessage}</p>
                )}
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading
                    ? 'Envoi en cours...'
                    : 'Envoyer le lien de réinitialisation'}
                </Button>
              </form>
            )}
            <div className="mt-4 text-center text-sm">
              <Link
                to="/login"
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                Retour à la connexion
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
